import { Link } from "react-router-dom";
import { useState, useEffect } from 'react';
import api from "../services/api";

export default function Home() {
  const [trendingStores, setTrendingStores] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        setLoading(true);
        // Fetch random stores and products
        const [storesResponse, productsResponse] = await Promise.all([
          api.get('/stores?limit=6'),
          api.get('/products?limit=8')
        ]);
        
        setTrendingStores(storesResponse.data.data.stores);
        setTrendingProducts(productsResponse.data.data.products);
      } catch (err) {
        console.error('Error fetching trending data:', err);
        setError('Failed to load trending content');
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingData();
  }, []);

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero section */}
        <div className="text-center py-16">
          <h1 className="text-5xl font-extrabold text-gray-900 mb-6">
            Create. Sell. Wear.
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            The easiest way for creators to launch their own fashion brand. No inventory, no hassle.
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/stores"
              className="bg-pink-600 hover:bg-pink-700 text-white px-8 py-3 rounded-md text-lg font-medium"
            >
              Shop Now
            </Link>
            <Link
              to="/start-store"
              className="bg-white hover:bg-gray-50 text-pink-600 border border-pink-600 px-8 py-3 rounded-md text-lg font-medium"
            >
              Start Selling
            </Link>
          </div>
        </div>

        {/* Trending Products */}
        <div className="py-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Trending Products
          </h2>
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {trendingProducts.map((product) => (
                <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <Link to={`/product/${product._id}`}>
                    <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
                      <img 
                        src={product.design?.image || product.design?.thumbnail || '/placeholder-product.jpg'} 
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{product.name}</h3>
                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">{product.description}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-pink-600 font-bold">
                          ${product.variants && product.variants.length > 0 
                            ? product.variants[0].finalPrice 
                            : 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {product.store?.name || 'Unknown Store'}
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Featured stores */}
        <div className="py-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Featured Creators
          </h2>
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {trendingStores.map((store) => (
                <div key={store._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <Link to={`/store/${store.slug}`}>
                    <div className="aspect-w-16 aspect-h-9 w-full overflow-hidden">
                      <img 
                        src={store.logo || '/placeholder-store.jpg'} 
                        alt={store.name}
                        className="w-full h-32 object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center">
                        <img 
                          src={store.logo?.url || '/placeholder-avatar.jpg'} 
                          alt={store.name}
                          className="w-12 h-12 rounded-full object-cover mr-3"
                        />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{store.name}</h3>
                          <p className="text-gray-600 text-sm">{store.category}</p>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm mt-2 line-clamp-2">{store.description}</p>
                      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                        <span>{store.stats?.totalProducts || 0} products</span>
                        <span>{store.stats?.totalSales || 0} sales</span>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="py-12 bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            How Trendle Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Create Your Designs",
                description: "Upload your artwork and customize products with our easy-to-use designer.",
                icon: "🎨",
              },
              {
                title: "Launch Your Store",
                description: "Set up your store in minutes with your own branding and domain.",
                icon: "🛍️",
              },
              {
                title: "We Handle the Rest",
                description: "When you get an order, we print, pack and ship it directly to your customer.",
                icon: "🚚",
              },
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{step.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}